// @ts-nocheck
/**
 * TeamLeader Workspace — Certificates Page
 * Issue course/track certificates to students in your led groups. Eligibility is
 * enforced server-side (api/teamleader/certificates/issue); ineligible issuance
 * surfaces the backend's message via the mutation's error toast.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import {
  fetchMyGroups,
  fetchAllAssignableTracks,
  fetchAllAssignableCourses,
} from "@/lib/api/teamleader";
import { fetchStudentGrid } from "@/lib/api/students";
import { PaginationControls } from "@/components/PaginationControls";
import { useIssueCertificate } from "@/hooks/use-teamleader";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Award,
  ArrowLeft,
  Search,
  Users,
  FolderOpen,
  BookOpen,
  GraduationCap,
  BadgeCheck,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/team-leader/certificates")({
  component: TeamLeaderCertificates,
});

function TeamLeaderCertificates() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [groupFilter, setGroupFilter] = useState("all");

  // Issue dialog state
  const [target, setTarget] = useState<any>(null); // the student being issued to
  const [certType, setCertType] = useState<"course" | "track">("course");
  const [sourceId, setSourceId] = useState("");
  const [customTitle, setCustomTitle] = useState("");
  const [customHours, setCustomHours] = useState("");

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const { data: groups = [], isLoading: groupsLoading } = useQuery({
    queryKey: ["tl", "groups"],
    queryFn: fetchMyGroups,
    enabled: !!user?.id,
    staleTime: 30_000,
  });

  // The flat, walk-every-page variants: this dropdown wants the whole catalogue, and the paged
  // fetchers would hand back a PagedResult envelope rather than an array.
  const { data: tracks = [] } = useQuery({
    queryKey: ["tl", "assignable-tracks"],
    queryFn: fetchAllAssignableTracks,
  });

  const { data: courses = [] } = useQuery({
    queryKey: ["tl", "assignable-courses"],
    queryFn: fetchAllAssignableCourses,
  });

  const issueMutation = useIssueCertificate();

  // Map of groupId -> Group details for quick lookup
  const groupMap = useMemo(() => {
    return new Map(groups.map((g) => [g.id, g]));
  }, [groups]);

  // Build grid query params
  const gridParams = useMemo(() => {
    const params: any = {
      pageNumber: page,
      pageSize: pageSize,
      search: search.trim(),
    };

    if (groupFilter !== "all") {
      params.groupId = groupFilter;
    }

    return params;
  }, [page, pageSize, search, groupFilter]);

  const { data: gridData, isLoading: studentsLoading } = useQuery({
    queryKey: ["tl", "students-grid-certs", gridParams],
    queryFn: () => fetchStudentGrid(gridParams),
    enabled: !!user?.id,
  });

  const students = gridData?.items || [];
  const totalStudentsCount = gridData?.totalCount || 0;

  const sources = certType === "course" ? courses : tracks;

  const openIssue = (student: any) => {
    setTarget(student);
    setCertType("course");
    setSourceId("");
    setCustomTitle("");
    setCustomHours("");
  };

  const closeIssue = () => setTarget(null);

  const submitIssue = () => {
    if (!target || !sourceId) return;
    issueMutation.mutate(
      {
        student_id: target.id,
        cert_type: certType,
        source_id: sourceId,
        custom_title: customTitle.trim() || null,
        custom_hours: customHours ? Number(customHours) : null,
      },
      { onSuccess: () => closeIssue() },
    );
  };

  const summary = [
    { label: "Students", value: totalStudentsCount, icon: Users, token: "var(--chart-1)" },
    { label: "Groups", value: groups.length, icon: FolderOpen, token: "var(--chart-2)" },
    { label: "Courses", value: courses.length, icon: GraduationCap, token: "var(--accent)" },
    { label: "Tracks", value: tracks.length, icon: BookOpen, token: "var(--chart-4)" },
  ];

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate({ to: "/team-leader/dashboard" })}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Certificates</h1>
          <p className="text-muted-foreground text-sm">
            Issue course and track certificates to students in your groups
          </p>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {summary.map((s) => (
          <Card key={s.label} className="border-t-[3px]" style={{ borderTopColor: s.token }}>
            <CardContent className="p-4 flex items-center gap-3">
              <div
                className="h-9 w-9 rounded-lg flex items-center justify-center"
                style={{ background: s.token }}
              >
                <s.icon className="h-4 w-4 text-white" />
              </div>
              <div>
                <p className="text-xl font-bold leading-none">{s.value}</p>
                <p className="text-xs text-muted-foreground mt-1">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search student by name or email..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="pl-10"
          />
        </div>
        <select
          className="h-10 px-3 py-2 border rounded-md text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
          value={groupFilter}
          onChange={(e) => {
            setGroupFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="all">All Groups</option>
          {groups.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </select>
      </div>

      {/* Roster */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <BadgeCheck className="h-4 w-4 text-primary" />
            Eligible Students
          </CardTitle>
          <CardDescription>
            Pick a student to issue a certificate. Eligibility rules are validated when you confirm.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {groupsLoading || studentsLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : students.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Award className="h-10 w-10 mx-auto mb-2 opacity-30" />
              <p className="font-semibold">No students found</p>
              <p className="text-sm mt-1">Adjust your search or group filter.</p>
            </div>
          ) : (
            <>
              <div className="grid gap-3">
                {students.map((s) => {
                  const primaryGroup = s.groups?.[0];
                  const groupName = primaryGroup?.groupName || "Unassigned";

                  return (
                    <div
                      key={s.id}
                      className="flex items-center justify-between p-3 border rounded-xl bg-card hover:bg-accent/30 transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div
                          className="h-10 w-10 rounded-full flex items-center justify-center font-bold text-white shrink-0"
                          style={{ background: "var(--gradient-brand)" }}
                        >
                          {(s.fullName || s.email || "?").charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-sm truncate">{s.fullName || s.email}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {groupName} · {s.email}
                          </p>
                        </div>
                      </div>
                      <Button size="sm" className="gap-2 shrink-0" onClick={() => openIssue({
                        id: s.id,
                        fullName: s.fullName,
                        email: s.email,
                        groupName: groupName,
                      })}>
                        <Award className="h-4 w-4" />
                        Issue
                      </Button>
                    </div>
                  );
                })}
              </div>

              {gridData && (
                <PaginationControls
                  pageNumber={page}
                  pageSize={pageSize}
                  totalPages={gridData.totalPages}
                  totalCount={gridData.totalCount}
                  hasNextPage={gridData.hasNextPage}
                  hasPreviousPage={gridData.hasPreviousPage}
                  onPageChange={setPage}
                  onPageSizeChange={(size) => {
                    setPageSize(size);
                    setPage(1);
                  }}
                  isLoading={studentsLoading}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Issue Certificate dialog */}
      <Dialog open={!!target} onOpenChange={(o) => !o && closeIssue()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Issue Certificate</DialogTitle>
            <DialogDescription>
              For {target?.fullName || target?.email} · {target?.groupName}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            {/* Type toggle */}
            <div className="grid grid-cols-2 gap-2">
              {(["course", "track"] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => {
                    setCertType(t);
                    setSourceId("");
                  }}
                  className={`flex items-center justify-center gap-2 p-3 rounded-lg border text-sm font-medium capitalize transition-colors ${
                    certType === t
                      ? "border-primary bg-primary/5 text-primary"
                      : "hover:bg-accent/50"
                  }`}
                >
                  {t === "course" ? (
                    <GraduationCap className="h-4 w-4" />
                  ) : (
                    <BookOpen className="h-4 w-4" />
                  )}
                  {t}
                </button>
              ))}
            </div>

            {/* Source select */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">
                {certType === "course" ? "Course" : "Track"}
              </label>
              <select
                className="w-full h-10 px-3 border rounded-md text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                value={sourceId}
                onChange={(e) => setSourceId(e.target.value)}
              >
                <option value="">Select a {certType}…</option>
                {sources.map((src: any) => (
                  <option key={src.id} value={src.id}>
                    {src.name}
                  </option>
                ))}
              </select>
              {sources.length === 0 && (
                <p className="text-xs text-warning">No assignable {certType}s available.</p>
              )}
            </div>

            {/* Optional overrides */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">
                  Custom title (optional)
                </label>
                <Input
                  placeholder="Defaults to source name"
                  value={customTitle}
                  onChange={(e) => setCustomTitle(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">
                  Custom hours (optional)
                </label>
                <Input
                  type="number"
                  min={0}
                  placeholder="e.g. 40"
                  value={customHours}
                  onChange={(e) => setCustomHours(e.target.value)}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeIssue}>
              Cancel
            </Button>
            <Button
              className="gap-2"
              disabled={!sourceId || issueMutation.isPending}
              onClick={submitIssue}
            >
              <Award className="h-4 w-4" />
              {issueMutation.isPending ? "Issuing…" : "Issue Certificate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
